import React, { useState, useEffect } from "react";
// SM-AUTH-HR: use the shared client so every call sends the login token.
// REVERT: change back to  import axios from "axios";
import axios from "./axios";
import Swal from "sweetalert2";
import Icon from "./ui/Icon";
import EmptyState from "./ui/EmptyState";
import "../styles/UnfreezePage.css";

export default function UnfreezePage() {
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [empId, setEmpId] = useState("");
    const [bulkMessage, setBulkMessage] = useState({ text: "", type: "" });
    const [individualMessage, setIndividualMessage] = useState({ text: "", type: "" });
    const [loading, setLoading] = useState(false);
    const [individualLoading, setIndividualLoading] = useState(false);
    const [unfrozenList, setUnfrozenList] = useState([]);

    // The window currently in force. Without showing this, "Set Window" was a
    // write-only action — there was no way to tell what was actually live.
    const [currentWindow, setCurrentWindow] = useState(null);
    const [closingWindow, setClosingWindow] = useState(false);

    // Load unfrozen employees on page load
    useEffect(() => {
        fetchUnfrozen();
        fetchCurrentWindow();
    }, []);

    const fetchCurrentWindow = async () => {
        try {
            const res = await axios.get("/api/Unfreeze/current-window");
            setCurrentWindow(res.data);
        } catch (err) {
            console.error("Error fetching current unfreeze window:", err);
        }
    };

    const handleCloseWindow = async () => {
        const confirmed = window.confirm(
            "Close the unfreeze window now?\n\n" +
            "Employees will no longer be able to submit or edit their skills."
        );
        if (!confirmed) return;

        setClosingWindow(true);
        try {
            const res = await axios.post("/api/Unfreeze/close-window");
            setBulkMessage({ text: res.data.message, type: "success" });
            await fetchCurrentWindow();
        } catch (err) {
            setBulkMessage({ text: "Could not close the window. Please try again.", type: "error" });
        } finally {
            setClosingWindow(false);
        }
    };

    const fetchUnfrozen = async () => {
        try {
            const res = await axios.get("/api/Unfreeze/unfrozen-employees");
            setUnfrozenList(res.data);
        } catch (err) {
            console.error("Error fetching unfrozen employees:", err);
        }
    };

    // ================= BULK UNFREEZE =================
    const handleBulkUnfreeze = async () => {
        if (!fromDate || !toDate) {
            setBulkMessage({
                text: "Please select both From and To dates.",
                type: "error"
            });
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const from = new Date(fromDate);
        const to = new Date(toDate);

        from.setHours(0, 0, 0, 0);
        to.setHours(0, 0, 0, 0);

        // Past From Date not allowed
        if (from < today) {
            setBulkMessage({
                text: "From Date cannot be in the past.",
                type: "error"
            });
            return;
        }

        // To Date also should not be in the past
        if (to < today) {
            setBulkMessage({
                text: "To Date cannot be in the past.",
                type: "error"
            });
            return;
        }

        // Existing validation
        if (from > to) {
            setBulkMessage({
                text: "From Date cannot be greater than To Date.",
                type: "error"
            });
            return;
        }

        setLoading(true);
        setBulkMessage({ text: "", type: "" });

        try {
            const res = await axios.post("/api/Unfreeze/set-window", {
                fromDate,
                toDate
            });

            setBulkMessage({
                text: res.data.message || "Unfreeze window set successfully!",
                type: "success"
            });

            // Setting a window replaces the previous one — show what is now live.
            await fetchCurrentWindow();
        } catch (err) {
            setBulkMessage({
                text: "Error setting unfreeze window. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    // ================= INDIVIDUAL UNFREEZE =================
    const handleIndividualUnfreeze = async () => {
        if (!empId.trim()) {
            setIndividualMessage({ text: "Please enter an Employee ID.", type: "error" });
            return;
        }

        setIndividualLoading(true);
        setIndividualMessage({ text: "", type: "" });

        try {
            const res = await axios.post("/api/Unfreeze/individual", {
                empId
            });

            setIndividualMessage({
                text: res.data.message || "Employee unfrozen successfully!",
                type: "success"
            });

            setEmpId("");

            // Refresh list instantly
            fetchUnfrozen();

        } catch (err) {
            setIndividualMessage({
                text: "Error unfreezing employee. Please try again.",
                type: "error"
            });
        } finally {
            setIndividualLoading(false);
        }
    };

    // ================= REFREEZE =================
    const handleRefreeze = async (empId) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: `Refreeze employee ${empId}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, refreeze",
            cancelButtonText: "Cancel"
        });

        if (!confirm.isConfirmed) return;

        try {
            await axios.post("/api/Unfreeze/refreeze", { empId });

            Swal.fire("Done!", "Employee refrozen successfully.", "success");

            // Refresh list
            fetchUnfrozen();

        } catch (err) {
            Swal.fire("Error", "Failed to refreeze employee.", "error");
        }
    };

    return (
        <div className="unfreeze-wrapper">
            <header className="page-head reveal">
                <span className="page-eyebrow">Admin</span>
                <h1 className="page-title-mt">Unfreeze Management</h1>
                <p className="page-sub">
                    Open submission windows for everyone, or unfreeze individual employees.
                </p>
            </header>

            {/* ================= BULK ================= */}
            <section className="card-mt card-pad reveal reveal-d1">
                <h2 className="card-title-mt">
                    <span className="card-title-icon"><Icon name="calendar" size={16} /></span>
                    Bulk Unfreeze by Date Range
                </h2>

                <p className="unfreeze-card-desc">
                    Select a date range to unfreeze all employees within that period.
                    Setting a window replaces the one currently in force.
                </p>

                {/* What is actually live right now. */}
                {currentWindow && (
                    <div
                        className={`notice ${currentWindow.hasWindow && currentWindow.isOpenToday
                            ? "notice-success"
                            : "notice-info"} unfreeze-current`}
                        role="status"
                    >
                        <Icon
                            name={currentWindow.hasWindow && currentWindow.isOpenToday ? "unlock" : "lock"}
                            size={17}
                        />
                        {currentWindow.hasWindow ? (
                            <span>
                                <strong>{currentWindow.status}</strong>
                                {" — "}
                                {new Date(currentWindow.fromDate).toLocaleDateString()} to{" "}
                                {new Date(currentWindow.toDate).toLocaleDateString()}
                                {currentWindow.isOpenToday && (
                                    <>
                                        {" · "}
                                        {currentWindow.daysRemaining === 0
                                            ? "last day"
                                            : `${currentWindow.daysRemaining} day(s) remaining`}
                                    </>
                                )}
                            </span>
                        ) : (
                            <span>
                                No unfreeze window is set. Employees cannot submit or edit
                                their skills.
                            </span>
                        )}

                        {currentWindow.hasWindow && (
                            <button
                                type="button"
                                className="btn btn-secondary-mt btn-sm unfreeze-close-btn"
                                onClick={handleCloseWindow}
                                disabled={closingWindow}
                            >
                                {closingWindow ? "Closing…" : "Close window"}
                            </button>
                        )}
                    </div>
                )}

                <div className="unfreeze-date-row">
                    <div className="unfreeze-field">
                        <label className="field-label" htmlFor="unfreeze-from">From date</label>
                        <input
                            id="unfreeze-from"
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="input-mt"
                        />
                    </div>

                    <div className="unfreeze-field">
                        <label className="field-label" htmlFor="unfreeze-to">To date</label>
                        <input
                            id="unfreeze-to"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="input-mt"
                        />
                    </div>

                    <div className="unfreeze-btn-wrapper">
                        <button
                            className="btn btn-primary-mt"
                            onClick={handleBulkUnfreeze}
                            disabled={loading}
                        >
                            <Icon name="unlock" size={16} />
                            {loading ? "Processing…" : "Set Window"}
                        </button>
                    </div>
                </div>

                {bulkMessage.text && (
                    <div
                        className={`notice ${bulkMessage.type === "success" ? "notice-success" : "notice-error"} unfreeze-notice`}
                        role="status"
                    >
                        <Icon name={bulkMessage.type === "success" ? "check" : "alertCircle"} size={17} />
                        <span>{bulkMessage.text}</span>
                    </div>
                )}
            </section>

            {/* Divider */}
            <div className="unfreeze-divider" aria-hidden="true">
                <span>OR</span>
            </div>

            {/* ================= INDIVIDUAL ================= */}
            <section className="card-mt card-pad reveal reveal-d2">
                <h2 className="card-title-mt">
                    <span className="card-title-icon"><Icon name="user" size={16} /></span>
                    Individual Unfreeze
                </h2>

                <p className="unfreeze-card-desc">
                    Enter a specific Employee ID to unfreeze that employee.
                </p>

                <div className="unfreeze-date-row">
                    <div className="unfreeze-field unfreeze-field-wide">
                        <label className="field-label" htmlFor="unfreeze-empid">Employee ID</label>
                        <input
                            id="unfreeze-empid"
                            type="text"
                            value={empId}
                            onChange={(e) => setEmpId(e.target.value)}
                            placeholder="e.g. MT01234"
                            className="input-mt"
                        />
                    </div>

                    <div className="unfreeze-btn-wrapper">
                        <button
                            className="btn btn-primary-mt"
                            onClick={handleIndividualUnfreeze}
                            disabled={individualLoading}
                        >
                            <Icon name="unlock" size={16} />
                            {individualLoading ? "Processing…" : "Unfreeze"}
                        </button>
                    </div>
                </div>

                {individualMessage.text && (
                    <div
                        className={`notice ${individualMessage.type === "success" ? "notice-success" : "notice-error"} unfreeze-notice`}
                        role="status"
                    >
                        <Icon name={individualMessage.type === "success" ? "check" : "alertCircle"} size={17} />
                        <span>{individualMessage.text}</span>
                    </div>
                )}
            </section>

            {/* ================= REFREEZE LIST ================= */}
            <section className="card-mt reveal reveal-d3">
                <div className="unfreeze-list-head">
                    <h2 className="card-title-mt">
                        <span className="card-title-icon"><Icon name="snowflake" size={16} /></span>
                        Refreeze Employees
                    </h2>
                </div>

                {unfrozenList.length === 0 ? (
                    <EmptyState
                        icon="snowflake"
                        title="No employees are currently unfrozen"
                        text="Employees you unfreeze individually will appear here."
                    />
                ) : (
                    <div className="table-wrap">
                        <table className="table-mt">
                            <thead>
                                <tr>
                                    <th scope="col">Emp ID</th>
                                    <th scope="col">From Date</th>
                                    <th scope="col">To Date</th>
                                    <th scope="col">Last Modified</th>
                                    <th scope="col">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {unfrozenList.map((emp) => (
                                    <tr key={emp.empId}>
                                        <td className="num">{emp.empId}</td>
                                        <td className="num">{new Date(emp.fromDate).toLocaleDateString()}</td>
                                        <td className="num">{new Date(emp.toDate).toLocaleDateString()}</td>
                                        <td className="num">{new Date(emp.lastModified).toLocaleString()}</td>
                                        <td>
                                            <button
                                                className="btn btn-danger-mt btn-sm"
                                                onClick={() => handleRefreeze(emp.empId)}
                                            >
                                                <Icon name="lock" size={14} />
                                                Refreeze
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
